import { Tooltip, TooltipTrigger } from "@components";
import {
	AlertCircle,
	AlertTriangle,
	CheckCircle,
	Clock,
	CreditCardCheck,
	FileQuestion02,
	XCircle,
} from "@untitledui/icons";
import type { ComponentType, SVGProps } from "react";
import { Image } from "@/components/ui/image";
import { IMAGES } from "../assets";
import type { InfractionListItem } from "../types/infraction.types";
import {
	type DeadlineStatusIconKind,
	getDeadlineStatusIconClassName,
	getDeadlineStatusIconKind,
} from "../utils/deadline-styles";
import {
	getDeadlineDateShort,
	getDeadlinePrimaryText,
	getInfractionOriginTooltipLabel,
	isManualInfractionOrigin,
} from "../utils/infraction-list-display";

type InfractionDeadlineCellProps = {
	item: InfractionListItem;
};

const DEADLINE_STATUS_ICONS: Record<
	Exclude<DeadlineStatusIconKind, "none">,
	ComponentType<SVGProps<SVGSVGElement>>
> = {
	clock: Clock,
	"alert-circle": AlertCircle,
	"alert-triangle": AlertTriangle,
	"check-circle": CheckCircle,
	"credit-card": CreditCardCheck,
	"file-question": FileQuestion02,
	"x-circle": XCircle,
};

function DeadlineStatusIcon({ item }: InfractionDeadlineCellProps) {
	const iconKind = getDeadlineStatusIconKind(item);

	if (iconKind === "none") {
		return null;
	}

	const Icon = DEADLINE_STATUS_ICONS[iconKind];

	return (
		<Icon
			aria-hidden
			className={`mt-0.5 size-3 shrink-0 ${getDeadlineStatusIconClassName(iconKind)}`}
		/>
	);
}

function InfractionOriginIcon({ item }: InfractionDeadlineCellProps) {
	const isManual = isManualInfractionOrigin(item);
	const tooltipLabel = getInfractionOriginTooltipLabel(item);
	const OriginIcon = isManual
		? IMAGES.IllustrationManualInfraction
		: IMAGES.IllustrationCogwheel;

	return (
		<Tooltip placement="top" title={tooltipLabel}>
			<TooltipTrigger
				aria-label={tooltipLabel}
				className="size-8 shrink-0 rounded-full bg-bg-secondary p-1.5 cursor-default!"
			>
				<Image
					alt={tooltipLabel}
					className="size-full text-fg-primary"
					height={32}
					src={OriginIcon}
					width={32}
				/>
			</TooltipTrigger>
		</Tooltip>
	);
}

export function InfractionDeadlineCell({ item }: InfractionDeadlineCellProps) {
	const primaryText = getDeadlinePrimaryText(item);
	const dateShort = getDeadlineDateShort(item);
	const hasDeadlineContent = primaryText !== "—";

	return (
		<div
			className="flex items-center gap-2"
			data-slot="infraction-deadline-cell"
		>
			<InfractionOriginIcon item={item} />

			{hasDeadlineContent ? (
				<div className="flex min-w-0 items-center gap-1.5">
					<DeadlineStatusIcon item={item} />
					<div className="flex min-w-0 flex-col">
						<span className="text-secondary text-sm leading-5">
							{primaryText}
						</span>
						{dateShort ? (
							<span className="text-[10px] text-tertiary leading-5">
								({dateShort})
							</span>
						) : null}
					</div>
				</div>
			) : (
				<span className="text-secondary text-sm leading-5">—</span>
			)}
		</div>
	);
}
